import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { snackbarColor } from '@/enums';
import { useRequiredAppSelector, useSnackbar } from '@/hooks';
import { useAttachEntityTag } from './useAttachEntityTag';
import { useCreateCharacter } from './useCharacterMutations';

type EntityType = 'act' | 'fragment' | 'scene';

interface CreateCharacterAndAttachData {
    name: string;
    age_category?: string;
    entityType: EntityType;
    entityId: number;
}

export function useCreateCharacterAndAttach() {
    const projectId = useRequiredAppSelector('projectId');
    const createCharacter = useCreateCharacter();
    const attachTag = useAttachEntityTag();
    const showSnackbar = useSnackbar();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async ({ name, age_category, entityType, entityId }: CreateCharacterAndAttachData) => {
            const result = await createCharacter.mutateAsync({
                project_id: projectId,
                data: {
                    name,
                    gender: 'other',
                    age_category,
                    tags: [],
                },
            });

            const newTagId = result.data.entity_tag.id;

            await attachTag.mutateAsync({
                entityType,
                entityId,
                addTags: [newTagId],
            });

            return result;
        },

        onError: error => {
            showSnackbar({
                message: t('character.errors.attach', { error: error instanceof Error ? error.message : String(error) }),
                open: true,
                severity: snackbarColor.error,
                title: t('error'),
            });
        },
    });
}
